/**
 * AppbarContext (#174)
 *
 * Permite que a página atual publique um SUBTÍTULO para a appbar mobile
 * (mockup `.m-ttl .t2`, ex: "Help Desk · Suporte Técnico"). O shell
 * (MainLayout) renderiza `título (t1)` + `subtítulo (t2)`. Quando a rota não
 * define subtítulo, só o título aparece (comportamento anterior).
 *
 * Deps do hook usam apenas `setSubtitle` (estável, do useState) — nunca o
 * objeto de contexto inteiro — pra não recriar efeito a cada render (evita o
 * loop que aconteceu no ModuleSearch).
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

interface AppbarContextValue {
  subtitle: string | null;
  setSubtitle: (value: string | null) => void;
}

const AppbarContext = createContext<AppbarContextValue | null>(null);

export const AppbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subtitle, setSubtitle] = useState<string | null>(null);
  const value = useMemo(() => ({ subtitle, setSubtitle }), [subtitle]);
  return <AppbarContext.Provider value={value}>{children}</AppbarContext.Provider>;
};

export const useAppbarSubtitleValue = (): string | null => useContext(AppbarContext)?.subtitle ?? null;

/** Título da appbar mobile: `t1` (rota) + `t2` (subtítulo do contexto, se houver). */
export const AppbarTitle: React.FC<{ title: string }> = ({ title }) => {
  const subtitle = useAppbarSubtitleValue();
  return (
    <div className="flex-1 min-w-0 flex flex-col justify-center">
      <span className="truncate font-bold text-[18px] leading-tight" style={{ color: 'var(--theme-text)' }}>
        {title}
      </span>
      {subtitle && (
        <span className="truncate text-[11px] leading-tight" style={{ color: 'var(--theme-muted-text)' }}>
          {subtitle}
        </span>
      )}
    </div>
  );
};

/** Página define o subtítulo da appbar mobile; limpa ao desmontar. */
export const useAppbarSubtitle = (subtitle: string | null): void => {
  const ctx = useContext(AppbarContext);
  const setSubtitle = ctx?.setSubtitle;
  useEffect(() => {
    if (!setSubtitle) return;
    setSubtitle(subtitle);
  }, [setSubtitle, subtitle]);
  useEffect(() => {
    if (!setSubtitle) return;
    return () => setSubtitle(null);
  }, [setSubtitle]);
};
